import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import { formatFullName } from '../../utils/nameUtils';
import { adminAPI, repAPI } from '../../services/api';
import ClientContacts from '../../components/internal/ClientContacts';
import ClientTimeline from '../../components/internal/ClientTimeline';
import ClientShippingAddresses from '../../components/internal/ClientShippingAddresses';

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const AdminClientDetailPage = ({ currentUser }) => {
  const { id } = useParams();
  const { org } = useOrg();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [fetchingClientDetails, setFetchingClientDetails] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [reps, setReps] = useState([]);
  const [loadingReps, setLoadingReps] = useState(false);
  const [clientRep, setClientRep] = useState(null);
  const [loadingClientRep, setLoadingClientRep] = useState(false);
  const [editClientData, setEditClientData] = useState({
    name: '',
    email: '',
    phone: '',
    practiceName: '',
    medicalLicense: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    status: 'ACTIVE',
    repId: '',
    internalNotes: '',
    // Billing address fields
    billingSameAsShipping: true,
    billingCompany: '',
    billingContactName: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZipCode: '',
    billingPhone: '',
    billingEmail: '',
  });

  useEffect(() => {
    fetchClient();
    loadReps();
  }, [id]);

  const loadReps = async () => {
    setLoadingReps(true);
    try {
      const response = await repAPI.getReps();
      if (response.success && response.data) {
        setReps(response.data);
      }
    } catch (error) {
      console.error('Failed to load reps:', error);
    } finally {
      setLoadingReps(false);
    }
  };

  const fetchClient = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getUserById(id);
      if (response.success) {
        setClient(response.data);
        // Fetch rep information
        fetchClientRep(id);
      } else {
        setError(response.error || 'Failed to load client');
      }
    } catch (err) {
      setError(err.message || 'Failed to load client');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientRep = async (clientId) => {
    try {
      setLoadingClientRep(true);
      const response = await repAPI.getClientRep(clientId);
      if (response.success && response.data?.rep) {
        setClientRep(response.data.rep);
      } else {
        setClientRep(null);
      }
    } catch (error) {
      console.error('Failed to load client rep:', error);
      setClientRep(null);
    } finally {
      setLoadingClientRep(false);
    }
  };

  const handleOpenEditDialog = async () => {
    if (!client) return;
    
    try {
      setFetchingClientDetails(true);
      // Fetch full client data
      const response = await adminAPI.getUserById(client.id);
      
      let user = client;
      if (response.success && response.data) {
        user = response.data;
      }

      // Load current rep assignment
      let currentRepId = '';
      try {
        const repResponse = await repAPI.getClientRep(client.id);
        if (repResponse.success && repResponse.data?.rep) {
          currentRepId = repResponse.data.rep.id;
        }
      } catch (error) {
        console.error('Failed to load client rep:', error);
      }

      setEditClientData({
        name: formatFullName(user) || user.email || '',
        email: user.email || '',
        phone: (user.phone && user.phone !== 'N/A') ? user.phone : '',
        practiceName: user.profile?.clinicName || '',
        medicalLicense: user.profile?.licenseNumber || '',
        address: user.profile?.address || '',
        city: user.profile?.city || '',
        state: user.profile?.state || '',
        zipCode: user.profile?.zipCode || '',
        status: user.status || 'ACTIVE',
        repId: currentRepId,
        internalNotes: user.internalNotes || user.profile?.internalNotes || '',
        // Billing address fields
        billingSameAsShipping: user.profile?.billingSameAsShipping !== false,
        billingCompany: user.profile?.billingCompany || '',
        billingContactName: user.profile?.billingContactName || '',
        billingAddress: user.profile?.billingAddress || '',
        billingCity: user.profile?.billingCity || '',
        billingState: user.profile?.billingState || '',
        billingZipCode: user.profile?.billingZipCode || '',
        billingPhone: user.profile?.billingPhone || '',
        billingEmail: user.profile?.billingEmail || '',
      });
      setEditClientOpen(true);
    } catch (error) {
      console.error('Failed to load client data:', error);
      setError('Failed to load client details');
    } finally {
      setFetchingClientDetails(false);
    }
  };

  const handleSaveClient = async () => {
    if (!client) return;

    try {
      setSavingClient(true);
      setError('');
      const [firstName = '', ...rest] = (editClientData.name || '').trim().split(' ');
      const lastName = rest.join(' ') || 'Client';

      const payload = {
        firstName,
        lastName,
        email: editClientData.email,
        phone: editClientData.phone,
        status: editClientData.status,
        internalNotes: editClientData.internalNotes || null,
        profile: {
          clinicName: editClientData.practiceName,
          licenseNumber: editClientData.medicalLicense,
          address: editClientData.address,
          city: editClientData.city,
          state: editClientData.state,
          zipCode: editClientData.zipCode,
          // Billing address fields
          billingSameAsShipping: editClientData.billingSameAsShipping,
          billingCompany: editClientData.billingSameAsShipping ? null : editClientData.billingCompany,
          billingContactName: editClientData.billingSameAsShipping ? null : editClientData.billingContactName,
          billingAddress: editClientData.billingSameAsShipping ? null : editClientData.billingAddress,
          billingCity: editClientData.billingSameAsShipping ? null : editClientData.billingCity,
          billingState: editClientData.billingSameAsShipping ? null : editClientData.billingState,
          billingZipCode: editClientData.billingSameAsShipping ? null : editClientData.billingZipCode,
          billingPhone: editClientData.billingSameAsShipping ? null : editClientData.billingPhone,
          billingEmail: editClientData.billingSameAsShipping ? null : editClientData.billingEmail,
        }
      };

      const response = await adminAPI.updateUser(client.id, payload);
      if (response.success) {
        // Update rep assignment if changed
        let currentRepId = '';
        try {
          const repResponse = await repAPI.getClientRep(client.id);
          if (repResponse.success && repResponse.data?.rep) {
            currentRepId = repResponse.data.rep.id;
          }
        } catch (error) {
          console.error('Failed to fetch current rep:', error);
        }
        
        if (editClientData.repId !== currentRepId) {
          if (editClientData.repId) {
            // Assign or reassign rep
            if (currentRepId) {
              // Reassign
              try {
                await repAPI.reassignClient(client.id, {
                  newRepUserId: editClientData.repId,
                  notes: 'Rep assignment updated by admin'
                });
              } catch (reassignError) {
                console.error('Reassign failed, trying direct assign:', reassignError);
                await repAPI.assignClient({
                  repUserId: editClientData.repId,
                  clientUserId: client.id,
                  notes: 'Rep assigned by admin'
                });
              }
            } else {
              // New assignment
              await repAPI.assignClient({
                repUserId: editClientData.repId,
                clientUserId: client.id,
                notes: 'Rep assigned by admin'
              });
            }
          } else if (currentRepId) {
            // Unassign
            try {
              await repAPI.reassignClient(client.id, {
                newRepUserId: null,
                notes: 'Rep unassigned by admin'
              });
            } catch (unassignError) {
              console.error('Unassign failed:', unassignError);
            }
          }
        }
        
        setEditClientOpen(false);
        setSuccess('Client updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        fetchClient(); // Refresh client data
        fetchClientRep(client.id); // Refresh rep data
      } else {
        setError(response.error || response.message || 'Failed to update client');
      }
    } catch (error) {
      console.error('Failed to update client:', error);
      setError(error.message || 'Failed to update client');
    } finally {
      setSavingClient(false);
    }
  };

  const handleActivityAdded = () => {
    fetchClient(); // Refresh to show new activity/note
  };

  const handleContactUpdated = () => {
    fetchClient(); // Refresh to show updated contacts
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: 'success',
      INACTIVE: 'default',
      PENDING: 'warning',
      REJECTED: 'error',
    };
    return colors[status] || 'default';
  };

  const formatStatus = (status) => {
    return status
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !client) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/internal/${org}/client-management`)}>
          Back to Clients
        </Button>
      </Box>
    );
  }

  if (!client) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">Client not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/internal/${org}/client-management`)} sx={{ mt: 2 }}>
          Back to Clients
        </Button>
      </Box>
    );
  }

  const clientName = formatFullName(client) || client.email || 'Client';
  const practiceName = client.profile?.clinicName || 'N/A';

  return (
    <Box
      sx={{
        pt: { xs: 7, md: 3 },
        minHeight: '100vh',
        maxWidth: '1600px',
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Top Bar */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/internal/${org}/client-management`)}>
            <ArrowBack />
          </IconButton>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: '#212121',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            {practiceName}
          </Typography>
          <Chip 
            label={formatStatus(client.status)} 
            color={getStatusColor(client.status)} 
            size="small"
          />
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column: Client Details */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
                Client Information
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={handleOpenEditDialog}
              >
                Edit Client
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Client Name
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'Poppins, sans-serif', 
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {clientName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Email
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'Poppins, sans-serif',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {client.email}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {client.phone || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Practice Name
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'Poppins, sans-serif', 
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {practiceName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Medical License
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'Poppins, sans-serif',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {client.profile?.licenseNumber || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, color: '#039582' }}>
                  📦 Primary/Shipping Address
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Address
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'Poppins, sans-serif',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {client.profile?.address || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  City
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {client.profile?.city || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  State
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {client.profile?.state || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Zip Code
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {client.profile?.zipCode || 'N/A'}
                </Typography>
              </Grid>

              {/* Billing Address Section - Always show */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, color: '#039582' }}>
                    💳 Billing Address
                  </Typography>
                  {(client.profile?.billingSameAsShipping !== false) && (
                    <Chip label="Same as Shipping" size="small" color="default" variant="outlined" />
                  )}
                </Box>
              </Grid>

              {client.profile?.billingSameAsShipping === false ? (
                <>
                  {client.profile?.billingCompany && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        Billing Company
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {client.profile.billingCompany}
                      </Typography>
                    </Grid>
                  )}

                  {client.profile?.billingContactName && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        Billing Contact
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {client.profile.billingContactName}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                      Billing Address
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                      {client.profile?.billingAddress || 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                      City
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                      {client.profile?.billingCity || 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                      State
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                      {client.profile?.billingState || 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                      Zip Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                      {client.profile?.billingZipCode || 'N/A'}
                    </Typography>
                  </Grid>

                  {(client.profile?.billingPhone || client.profile?.billingEmail) && (
                    <>
                      {client.profile?.billingPhone && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                            Billing Phone
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                            {client.profile.billingPhone}
                          </Typography>
                        </Grid>
                      )}
                      {client.profile?.billingEmail && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                            Billing Email
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                            {client.profile.billingEmail}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif', color: 'text.secondary', fontStyle: 'italic' }}>
                    Using shipping address for billing. Click "Edit Client" to set a different billing address.
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Assigned Sales Rep
                </Typography>
                {loadingClientRep ? (
                  <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif', color: 'text.secondary' }}>
                    Loading...
                  </Typography>
                ) : clientRep ? (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'Poppins, sans-serif', 
                      fontWeight: 500,
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                  >
                    {formatFullName(clientRep) || clientRep.email || 'Unknown Rep'}
                  </Typography>
                ) : (
                  <Typography variant="body1" sx={{ fontFamily: 'Poppins, sans-serif', color: 'text.secondary', fontStyle: 'italic' }}>
                    No Rep Assigned
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip 
                    label={formatStatus(client.status)} 
                    color={getStatusColor(client.status)} 
                    size="small"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  Created
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                  {new Date(client.createdAt).toLocaleString()}
                </Typography>
              </Grid>

              {client.lastLoginAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Last Login
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    {new Date(client.lastLoginAt).toLocaleString()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Contacts Section */}
          <Box sx={{ mt: 3 }}>
            <ClientContacts client={client} onContactUpdated={handleContactUpdated} />
          </Box>

          {/* Shipping Addresses Section */}
          <Box sx={{ mt: 3 }}>
            <ClientShippingAddresses client={client} onAddressUpdated={fetchClient} />
          </Box>
        </Grid>

        {/* Right Column: Timeline */}
        <Grid item xs={12} md={5}>
          <ClientTimeline 
            client={client} 
            onActivityAdded={handleActivityAdded}
          />
        </Grid>
      </Grid>

      {/* Edit Client Dialog */}
      <Dialog 
        open={editClientOpen} 
        onClose={() => !savingClient && setEditClientOpen(false)} 
        maxWidth="md" 
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Edit {org === 'bare' ? 'Bare Biologics' : 'EPL'} Client Account</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            {fetchingClientDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Update client information. Changes will be saved immediately.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={editClientData.name}
                      onChange={(e) => setEditClientData({ ...editClientData, name: e.target.value })}
                      required
                      disabled={savingClient}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={editClientData.email}
                      onChange={(e) => setEditClientData({ ...editClientData, email: e.target.value })}
                      required
                      disabled={savingClient}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={editClientData.phone}
                      onChange={(e) => setEditClientData({ ...editClientData, phone: e.target.value })}
                      required
                      disabled={savingClient}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Practice Name"
                      value={editClientData.practiceName}
                      onChange={(e) => setEditClientData({ ...editClientData, practiceName: e.target.value })}
                      required
                      disabled={savingClient}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Medical License Number"
                      value={editClientData.medicalLicense}
                      onChange={(e) => setEditClientData({ ...editClientData, medicalLicense: e.target.value })}
                      required
                      disabled={savingClient}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Sales Rep</InputLabel>
                      <Select
                        value={editClientData.repId || ''}
                        onChange={(e) => setEditClientData({ ...editClientData, repId: e.target.value })}
                        disabled={savingClient || loadingReps}
                        label="Sales Rep"
                      >
                        <MenuItem value="">
                          <em>No Rep Assigned</em>
                        </MenuItem>
                        {reps.map((rep) => (
                          <MenuItem key={rep.id} value={rep.id}>
                            {formatFullName(rep) || rep.email}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={editClientData.address}
                      onChange={(e) => setEditClientData({ ...editClientData, address: e.target.value })}
                      required
                      disabled={savingClient}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={editClientData.city}
                      onChange={(e) => setEditClientData({ ...editClientData, city: e.target.value })}
                      required
                      disabled={savingClient}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      select
                      label="State"
                      value={editClientData.state}
                      onChange={(e) => setEditClientData({ ...editClientData, state: e.target.value })}
                      required
                      disabled={savingClient}
                    >
                      {US_STATES.map((state) => (
                        <MenuItem key={state.value} value={state.value}>
                          {state.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={editClientData.zipCode}
                      onChange={(e) => setEditClientData({ ...editClientData, zipCode: e.target.value })}
                      required
                      disabled={savingClient}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editClientData.status === 'ACTIVE'}
                          onChange={(e) => setEditClientData({ ...editClientData, status: e.target.checked ? 'ACTIVE' : 'INACTIVE' })}
                          color="success"
                          disabled={savingClient}
                        />
                      }
                      label={
                        <Typography variant="body1" sx={{ fontWeight: 500, color: editClientData.status === 'ACTIVE' ? 'success.main' : 'text.secondary' }}>
                          {editClientData.status === 'ACTIVE' ? 'Active Account' : 'Inactive Account'}
                        </Typography>
                      }
                    />
                  </Grid>

                  {/* Billing Address Section */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                      Billing Address
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editClientData.billingSameAsShipping}
                          onChange={(e) => setEditClientData({ ...editClientData, billingSameAsShipping: e.target.checked })}
                          color="primary"
                          disabled={savingClient}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Same as shipping address
                        </Typography>
                      }
                    />
                  </Grid>

                  {!editClientData.billingSameAsShipping && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Billing Company"
                          value={editClientData.billingCompany}
                          onChange={(e) => setEditClientData({ ...editClientData, billingCompany: e.target.value })}
                          disabled={savingClient}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Billing Contact Name"
                          value={editClientData.billingContactName}
                          onChange={(e) => setEditClientData({ ...editClientData, billingContactName: e.target.value })}
                          disabled={savingClient}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Billing Address"
                          value={editClientData.billingAddress}
                          onChange={(e) => setEditClientData({ ...editClientData, billingAddress: e.target.value })}
                          disabled={savingClient}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="City"
                          value={editClientData.billingCity}
                          onChange={(e) => setEditClientData({ ...editClientData, billingCity: e.target.value })}
                          disabled={savingClient}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          select
                          label="State"
                          value={editClientData.billingState}
                          onChange={(e) => setEditClientData({ ...editClientData, billingState: e.target.value })}
                          disabled={savingClient}
                        >
                          {US_STATES.map((state) => (
                            <MenuItem key={state.value} value={state.value}>
                              {state.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          label="ZIP Code"
                          value={editClientData.billingZipCode}
                          onChange={(e) => setEditClientData({ ...editClientData, billingZipCode: e.target.value })}
                          disabled={savingClient}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Billing Phone"
                          value={editClientData.billingPhone}
                          onChange={(e) => setEditClientData({ ...editClientData, billingPhone: e.target.value })}
                          disabled={savingClient}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Billing Email"
                          type="email"
                          value={editClientData.billingEmail}
                          onChange={(e) => setEditClientData({ ...editClientData, billingEmail: e.target.value })}
                          disabled={savingClient}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                      Internal Notes (Not visible to client)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Internal Notes"
                      placeholder="Add private notes about this client (e.g., special instructions, preferences, account history)"
                      value={editClientData.internalNotes}
                      onChange={(e) => setEditClientData({ ...editClientData, internalNotes: e.target.value })}
                      disabled={savingClient}
                      helperText="These notes are only visible to internal users and will never be shown to the client"
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditClientOpen(false)} disabled={savingClient}>Cancel</Button>
          <Button
            onClick={handleSaveClient}
            variant="contained"
            disabled={!editClientData.name || !editClientData.email || savingClient || fetchingClientDetails}
            sx={{
              bgcolor: '#039582',
              '&:hover': { bgcolor: '#027A6B' }
            }}
          >
            {savingClient ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminClientDetailPage;

